import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { CellResponseWithClonotype } from '../generated/generatedOpenAPI';
import { AppState } from '../store/store';

export function useFilteredCellsWithClonotype<T extends CellResponseWithClonotype>({
  data,
  options,
}: {
  data: T[] | undefined;
  options?: {
    skipClonotypeFilters?: boolean;
  };
}): T[] {
  const { id: experimentId } = useParams<{ id: string }>();

  const clonotypeFilters = useSelector((state: AppState) => state.clonotypes[experimentId as string]?.filters ?? []);

  // Clonotype filtering logic (conditionally applied)
  const finalFilteredData = React.useMemo(() => {
    if (!data) {
      return [];
    }

    if (options?.skipClonotypeFilters || clonotypeFilters.length === 0) {
      return data;
    }

    return data.filter((item) => {
      for (const filter of clonotypeFilters) {
        const filterId = filter.id as keyof T;
        const filterValue = filter.value;
        const valueToCheck = item[filterId];
        if (!valueToCheck) {
          return false;
        }

        if (typeof valueToCheck === 'number' && filterValue) {
          const { min, max, exact } = filterValue as { min?: number; max?: number; exact?: number };
          if ((min && valueToCheck < min) || (max && valueToCheck > max) || (exact && valueToCheck !== exact)) {
            return false;
          }
        }

        if (Array.isArray(filterValue) && filterValue.length > 0 && !filterValue.includes(valueToCheck)) {
          return false;
        }

        if (typeof filterValue === 'string') {
          if (filterValue.includes(',')) {
            const filterValues = filterValue.split(',');
            if (!filterValues.includes(valueToCheck?.toString())) {
              return false;
            }
          } else if (!valueToCheck?.toString().includes(filterValue as string)) {
            return false;
          }
        }
      }
      return true;
    });
  }, [data, clonotypeFilters, options?.skipClonotypeFilters]);

  return finalFilteredData;
}
